import { createSeededRandom } from '../utils/math';
import { Emitter } from './Events';
import { TaxiMission, type FareSummary } from './TaxiMission';
import { pickRandomSite, type SidewalkTile } from './PickupSites';

export const PICKUP_RADIUS = 3;
export const RESULT_CARD_DURATION_SEC = 2.5;

export type MissionState = 'idle' | 'awaiting_pickup' | 'en_route' | 'result_card';

export interface ShiftStats {
  rides: number;
  earnings: number;
  bestFare: number;
  cleanRides: number;
  longestStreak: number;
  currentStreak: number;
}

export interface ShiftSnapshot extends ShiftStats {
  state: MissionState;
  pickupTarget: SidewalkTile | null;
  dropoffTarget: SidewalkTile | null;
  timeRemaining: number | null;
  lastFare: FareSummary | null;
}

interface MissionEvents extends Record<string, unknown> {
  stateChanged: ShiftSnapshot;
  tick: ShiftSnapshot;
  toast: { message: string; tone: 'info' | 'success' | 'warn' };
  shiftEnded: ShiftStats;
}

const initialStats = (): ShiftStats => ({
  rides: 0, earnings: 0, bestFare: 0, cleanRides: 0, longestStreak: 0, currentStreak: 0,
});

export class MissionManager extends Emitter<MissionEvents> {
  state: MissionState = 'idle';
  stats: ShiftStats = initialStats();
  currentMission: TaxiMission | null = null;
  lastDropoff: SidewalkTile | null = null;
  lastFare: FareSummary | null = null;
  private rng: () => number = createSeededRandom(1);
  private resultCardTimer = 0;

  startShift() {
    if (this.state !== 'idle') return;
    this.stats = initialStats();
    this.rng = createSeededRandom(Date.now() | 0);
    this.lastDropoff = null;
    this.lastFare = null;
    this.spawnPickup();
    this.emit('toast', { message: 'Shift started', tone: 'success' });
  }

  endShift(): ShiftStats {
    const stats = this.stats;
    this.state = 'idle';
    this.currentMission = null;
    this.emit('shiftEnded', stats);
    this.emit('stateChanged', this.snapshot());
    return stats;
  }

  abandonRide() {
    if (this.state !== 'en_route' && this.state !== 'awaiting_pickup') return;
    this.currentMission = null;
    this.stats.currentStreak = 0;
    this.spawnPickup();
    this.emit('toast', { message: 'Ride abandoned — no fare', tone: 'warn' });
  }

  onCrash() {
    if (this.state === 'en_route' && this.currentMission) {
      const wasClean = this.currentMission.clean;
      this.currentMission.onCrash();
      if (wasClean) {
        this.emit('toast', { message: 'Clean bonus lost', tone: 'warn' });
      }
    }
  }

  update(delta: number, carX: number, carZ: number) {
    if (this.state === 'idle') return;

    if (this.state === 'result_card') {
      this.resultCardTimer += delta;
      if (this.resultCardTimer >= RESULT_CARD_DURATION_SEC) {
        this.spawnPickup();
      }
      this.emit('tick', this.snapshot());
      return;
    }

    if (this.state === 'awaiting_pickup' && this.currentMission) {
      const tgt = this.currentMission.pickup;
      if (Math.hypot(tgt.curbX - carX, tgt.curbZ - carZ) < PICKUP_RADIUS) {
        this.state = 'en_route';
        this.emit('stateChanged', this.snapshot());
        this.emit('toast', { message: 'Passenger onboard — Destination set', tone: 'success' });
      }
    } else if (this.state === 'en_route' && this.currentMission) {
      this.currentMission.tick(delta);
      const tgt = this.currentMission.destination;
      if (Math.hypot(tgt.curbX - carX, tgt.curbZ - carZ) < PICKUP_RADIUS) {
        this.completeRide();
      }
    }
    this.emit('tick', this.snapshot());
  }

  private spawnPickup() {
    const pickup = pickRandomSite(this.rng, {
      minDistanceFrom: this.lastDropoff ? { x: this.lastDropoff.x, z: this.lastDropoff.z } : undefined,
      minTiles: 6,
    });
    const destination = pickRandomSite(this.rng, {
      minDistanceFrom: { x: pickup.x, z: pickup.z },
      minTiles: 6,
      excludeRowCol: { row: pickup.row, col: pickup.col },
    });
    this.currentMission = new TaxiMission(pickup, destination);
    this.state = 'awaiting_pickup';
    this.resultCardTimer = 0;
    this.emit('stateChanged', this.snapshot());
  }

  private completeRide() {
    if (!this.currentMission) return;
    const fare = this.currentMission.complete();
    this.lastFare = fare;
    this.lastDropoff = this.currentMission.destination;
    this.stats.rides += 1;
    this.stats.earnings += fare.total;
    if (fare.total > this.stats.bestFare) this.stats.bestFare = fare.total;
    if (fare.clean) {
      this.stats.cleanRides += 1;
      this.stats.currentStreak += 1;
      if (this.stats.currentStreak > this.stats.longestStreak) {
        this.stats.longestStreak = this.stats.currentStreak;
      }
    } else {
      this.stats.currentStreak = 0;
    }
    this.currentMission = null;
    this.state = 'result_card';
    this.resultCardTimer = 0;
    this.emit('stateChanged', this.snapshot());
  }

  snapshot(): ShiftSnapshot {
    return {
      ...this.stats,
      state: this.state,
      pickupTarget: this.currentMission?.pickup ?? null,
      dropoffTarget: this.currentMission?.destination ?? null,
      timeRemaining: this.currentMission
        ? Math.max(0, this.currentMission.expectedSec - this.currentMission.timeElapsed)
        : null,
      lastFare: this.state === 'result_card' ? this.lastFare : null,
    };
  }
}
